/**
 * The four sentences the PWA is ever allowed to say.
 *
 *   "You're offline."
 *   "Showing saved data from 4:12 pm."
 *   "A new version is ready."
 *   "Install TeamCaddy."
 *
 * Built as plain DOM rather than as a React component on purpose: it has to be
 * able to appear when the app's own bundle has failed to load, and it must not
 * make the shell re-render. It reads tokens like everything else, so it is the
 * same surface, hairline and type scale as the rest of the product.
 *
 * The two rules it is designed against:
 *
 * 1. **Never claim fresh data is fresh.** The staleness line only ever appears
 *    because the service worker said, of a specific response, that it came
 *    from disk — and it names the time that copy was received. It clears the
 *    moment any read is answered by the network.
 * 2. **Never nag.** One install offer. Dismissed is dismissed, remembered on
 *    the device, and the offer never interrupts anything: it sits at the
 *    bottom of the screen, above the tab bar, and can be ignored forever.
 */

/** Once dismissed, the install offer stays gone for this long. */
const INSTALL_SNOOZE_MS = 90 * 24 * 60 * 60 * 1000;
const INSTALL_KEY = "tc-install-dismissed-at";

const CSS = `
.tc-pwa {
  position: fixed;
  z-index: var(--ui-layer-toast);
  left: var(--space-3);
  right: var(--space-3);
  /* Fixed positioning is measured from the viewport, not from the body's
     safe-area padding, so the inset has to be paid for again here — and on a
     phone the bottom tab bar is already standing in that space. The bar is one
     tap target plus its own padding, so clearing it means clearing that. */
  bottom: calc(env(safe-area-inset-bottom) + var(--layout-min-tap-target) + var(--space-6));
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  pointer-events: none;
  font-family: var(--font-sans);
}

@media (min-width: 1024px) {
  .tc-pwa {
    right: auto;
    max-width: var(--ui-panel-sm);
    bottom: calc(env(safe-area-inset-bottom) + var(--space-4));
  }
}

.tc-pwa > * { pointer-events: auto; }

.tc-pwa-status {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  border: var(--ui-hairline) solid var(--border-subtle);
  border-radius: var(--radius-full);
  background: var(--surface);
  box-shadow: var(--shadow-sm);
  color: var(--text-muted);
  font-size: var(--type-small-size);
  line-height: var(--type-small-line-height);
  letter-spacing: var(--type-small-tracking);
  min-height: var(--ui-control-md);
}

.tc-pwa-dot {
  width: var(--ui-dot);
  height: var(--ui-dot);
  border-radius: var(--radius-full);
  background: var(--text-faint);
  flex: none;
}

.tc-pwa-card {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
  padding: var(--space-3);
  border: var(--ui-hairline) solid var(--border-subtle);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow-lg);
  color: var(--text-default);
  font-size: var(--type-small-size);
  line-height: var(--type-small-line-height);
  letter-spacing: var(--type-small-tracking);
  max-width: 100%;
}

.tc-pwa-text { flex: 1 1 auto; min-width: 0; color: var(--text-primary); }

.tc-pwa-actions { display: flex; gap: var(--space-2); flex: none; }

.tc-pwa-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: var(--ui-control-md);
  padding-inline: var(--space-3);
  border-radius: var(--radius-md);
  border: var(--ui-hairline) solid var(--border-subtle);
  background: var(--surface);
  color: var(--text-default);
  font: inherit;
  font-weight: var(--weight-medium);
  letter-spacing: inherit;
  cursor: pointer;
  transition: var(--motion-hover);
}

.tc-pwa-btn:hover { background: var(--surface-hover); color: var(--text-primary); }

.tc-pwa-btn-primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--text-on-brand);
}

.tc-pwa-btn-primary:hover { background: var(--accent-hover); border-color: var(--accent-hover); color: var(--text-on-brand); }

.tc-pwa-btn:focus-visible {
  outline: var(--focus-outline);
  outline-offset: var(--focus-offset);
}

/* Every control in this thing is reachable by a wet thumb in November. */
@media (pointer: coarse) {
  .tc-pwa-btn { min-height: var(--ui-control-touch); }
  .tc-pwa-status { min-height: var(--ui-control-touch); border-radius: var(--radius-lg); }
}
`;

type StatusKind = "offline" | "stale" | null;

let root: HTMLElement | null = null;
let statusEl: HTMLElement | null = null;
let cardEl: HTMLElement | null = null;
let staleSince: Date | null = null;

function ensureRoot(): HTMLElement {
  if (root) return root;
  const style = document.createElement("style");
  style.textContent = CSS;
  document.head.append(style);
  root = document.createElement("div");
  root.className = "tc-pwa";
  document.body.append(root);
  return root;
}

function timeOfDay(at: Date): string {
  return at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** The one status line. Offline outranks staleness; nothing else is shown. */
function renderStatus(): void {
  const kind: StatusKind = !navigator.onLine ? "offline" : staleSince ? "stale" : null;

  if (!kind) {
    statusEl?.remove();
    statusEl = null;
    return;
  }

  const text =
    kind === "offline"
      ? staleSince
        ? `Offline — showing saved data from ${timeOfDay(staleSince)}`
        : "Offline — showing what was already loaded"
      : `Showing saved data from ${timeOfDay(staleSince as Date)}`;

  if (!statusEl) {
    statusEl = document.createElement("div");
    statusEl.className = "tc-pwa-status";
    // Polite: this is context for what is already on screen, not an alert.
    statusEl.setAttribute("role", "status");
    statusEl.setAttribute("aria-live", "polite");
    const dot = document.createElement("span");
    dot.className = "tc-pwa-dot";
    const label = document.createElement("span");
    label.dataset.label = "";
    statusEl.append(dot, label);
    ensureRoot().prepend(statusEl);
  }
  const label = statusEl.querySelector<HTMLElement>("[data-label]");
  if (label) label.textContent = text;
}

/** The service worker answered a read from disk, at this moment in the past. */
export function markServedFromCache(cachedAt: string | null): void {
  const at = cachedAt ? new Date(cachedAt) : new Date();
  staleSince = Number.isNaN(at.getTime()) ? new Date() : at;
  renderStatus();
}

/** A read was answered by the network: whatever is on screen is current. */
export function markServedFromNetwork(): void {
  if (!staleSince) return;
  staleSince = null;
  renderStatus();
}

export function watchConnection(): void {
  addEventListener("online", renderStatus);
  addEventListener("offline", renderStatus);
  renderStatus();
}

/** One card at a time, and only ever one. */
function showCard(text: string, actions: { label: string; primary?: boolean; run: () => void }[]): void {
  cardEl?.remove();
  cardEl = document.createElement("div");
  cardEl.className = "tc-pwa-card";
  const p = document.createElement("span");
  p.className = "tc-pwa-text";
  p.textContent = text;
  const group = document.createElement("div");
  group.className = "tc-pwa-actions";
  for (const action of actions) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = action.primary ? "tc-pwa-btn tc-pwa-btn-primary" : "tc-pwa-btn";
    btn.textContent = action.label;
    btn.addEventListener("click", action.run);
    group.append(btn);
  }
  cardEl.append(p, group);
  ensureRoot().append(cardEl);
}

function dismissCard(): void {
  cardEl?.remove();
  cardEl = null;
}

/**
 * A newer build is installed and waiting.
 *
 * It is deliberately an offer, not an event. Swapping the running app's assets
 * underneath it — which is what an automatic `skipWaiting` does — means the
 * next lazily-loaded route comes from a different build than the one that
 * asked for it, and the failure lands on whoever happens to be halfway through
 * a form. "Later" is a real answer: the update applies on the next cold start.
 */
export function offerUpdate(apply: () => void): void {
  showCard("A new version of TeamCaddy is ready.", [
    { label: "Reload", primary: true, run: apply },
    { label: "Later", run: dismissCard },
  ]);
}

export function installDismissed(): boolean {
  try {
    const at = Number(localStorage.getItem(INSTALL_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < INSTALL_SNOOZE_MS;
  } catch {
    // Private browsing throws on any access. Treat it as "not dismissed", but
    // note that it also means the dismissal below will not persist — which is
    // the correct trade for a session that keeps nothing anyway.
    return false;
  }
}

function rememberDismissal(): void {
  try {
    localStorage.setItem(INSTALL_KEY, String(Date.now()));
  } catch {
    /* see above */
  }
}

export function offerInstall(prompt: () => void): void {
  // Never on top of an update offer — the update matters more, and two cards
  // stacked at the bottom of a phone is an interstitial with extra steps.
  if (cardEl) return;
  showCard("Add TeamCaddy to your home screen.", [
    {
      label: "Install",
      primary: true,
      run: () => {
        rememberDismissal();
        dismissCard();
        prompt();
      },
    },
    {
      label: "Not now",
      run: () => {
        rememberDismissal();
        dismissCard();
      },
    },
  ]);
}

export function clearInstallOffer(): void {
  rememberDismissal();
  dismissCard();
}
